import { useEffect, useRef, useState } from "react";

export type VoiceState = "idle" | "recording" | "listening" | "error";
type State = VoiceState;

/**
 * Speak instead of typing. The recording is sent once, turned into text, and placed in the
 * answer so the maker can read and correct it before continuing. Nothing is stored.
 */
export function VoiceInput({
  transcribe,
  onText,
  disabled,
  onLevel,
  onState,
}: {
  transcribe: (recording: Blob) => Promise<string>;
  onText: (text: string) => void;
  disabled?: boolean;
  /** The loudness of the maker's voice while recording, 0..1, for Caprica's iris. */
  onLevel?: (level: number) => void;
  onState?: (state: VoiceState) => void;
}) {
  const [state, setState] = useState<State>("idle");
  const [message, setMessage] = useState("");
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef(0);
  const meter = useRef<() => void>(() => {});
  const stateHandler = useRef(onState);
  stateHandler.current = onState;
  const supported =
    typeof window !== "undefined" && "MediaRecorder" in window && !!navigator.mediaDevices?.getUserMedia;

  useEffect(() => stateHandler.current?.(state), [state]);
  useEffect(
    () => () => {
      meter.current();
      window.clearInterval(timer.current);
      recorder.current?.stream.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  if (!supported) return null;

  const start = async () => {
    setMessage("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const media = new MediaRecorder(stream);
      meter.current = listenToLevel(stream, onLevel);
      chunks.current = [];
      media.ondataavailable = (event) => {
        if (event.data.size) chunks.current.push(event.data);
      };
      media.onstop = async () => {
        meter.current();
        stream.getTracks().forEach((track) => track.stop());
        window.clearInterval(timer.current);
        const recording = new Blob(chunks.current, { type: media.mimeType || "audio/webm" });
        if (!recording.size) {
          setState("idle");
          return;
        }
        setState("listening");
        try {
          const text = (await transcribe(recording)).trim();
          if (text) onText(text);
          else setMessage("I didn't catch anything. Try again a little closer to the microphone.");
          setState(text ? "idle" : "error");
        } catch (failure) {
          setMessage(failure instanceof Error ? failure.message : "Caprica could not hear that recording.");
          setState("error");
        }
      };
      recorder.current = media;
      media.start();
      setSeconds(0);
      timer.current = window.setInterval(() => setSeconds((value) => {
        // Keep a spoken answer short enough to read back comfortably.
        if (value + 1 >= 300) media.stop();
        return value + 1;
      }), 1000);
      setState("recording");
    } catch {
      setMessage("The microphone is not available. Allow it in the browser, or type your answer.");
      setState("error");
    }
  };

  const recording = state === "recording";
  return (
    <div className="voice-input" data-state={state}>
      <button
        type="button"
        className="voice-input-button"
        aria-pressed={recording}
        aria-label={recording ? "Stop and use what I said" : "Speak your answer"}
        disabled={disabled || state === "listening"}
        onClick={() => (recording ? recorder.current?.stop() : void start())}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          {recording ? (
            <rect x="7" y="7" width="10" height="10" rx="2" />
          ) : (
            <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Zm-7 9a7 7 0 0 0 14 0M12 19v3" />
          )}
        </svg>
      </button>
      <span className="voice-input-status" role="status">
        {recording
          ? `Listening… ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")} · tap to finish`
          : state === "listening"
            ? "Writing down what you said…"
            : message}
      </span>
    </div>
  );
}

/** Reads the microphone's loudness each frame; returns the function that stops listening. */
function listenToLevel(stream: MediaStream, onLevel?: (level: number) => void): () => void {
  const Context = typeof window !== "undefined" ? window.AudioContext : undefined;
  if (!onLevel || !Context) return () => {};
  try {
    const context = new Context();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    let frame = 0;
    const read = () => {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += ((sample - 128) / 128) ** 2;
      onLevel(Math.min(1, Math.sqrt(sum / samples.length) * 4.5));
      frame = requestAnimationFrame(read);
    };
    frame = requestAnimationFrame(read);
    return () => {
      cancelAnimationFrame(frame);
      onLevel(0);
      void context.close().catch(() => {});
    };
  } catch {
    return () => {};
  }
}
